import { type KeyboardEvent, useEffect, useId, useMemo, useState } from 'react'

import { AutoComplete, Spin } from 'antd'

import { Label } from 'common/components/Label/Label'

import type { MedicalIssue } from 'features/Schedule/api/medicalIssues.types'

import type { PickerEntry, PickerKey } from './issuePicker.types'
import {
  PICKER_TEXT,
  PROCEDURE_SEARCH_MIN_CHARS,
  filterPatientProblems,
  getPickerKey,
} from './issuePicker.utils'
import styles from './medicalissues.module.scss'
import { PickerChips } from './PickerChips'
import { type PickerOption, buildPickerOptions, getNotFoundMessage } from './pickerOptions'
import { useProcedureSearch } from './useProcedureSearch'

export type ProblemListPickerProps = {
  siteId: string
  hasPatient: boolean
  patientIssues: MedicalIssue[]
  chosen: PickerEntry[]
  onPick: (entry: PickerEntry) => void
  onRemove: (key: PickerKey) => void
  disabled?: boolean
}

const getHint = (hasPatient: boolean, hasChosen: boolean): string | null => {
  if (!hasPatient) return PICKER_TEXT.choosePatient
  return hasChosen ? null : PICKER_TEXT.nothingRecorded
}

export const ProblemListPicker = ({
  siteId,
  hasPatient,
  patientIssues,
  chosen,
  onPick,
  onRemove,
  disabled = false,
}: ProblemListPickerProps) => {
  const searchId = useId()
  const [term, setTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Like the UPC select, the field waits disabled until it has something to search in.
  const isDisabled = disabled || !hasPatient

  // antd never reports a close while disabled, so the open flag must be reset here.
  useEffect(() => {
    if (isDisabled) setIsOpen(false)
  }, [isDisabled])
  const showProcedures = term.trim().length >= PROCEDURE_SEARCH_MIN_CHARS
  const { procedures, isSearching } = useProcedureSearch({
    term,
    siteId,
    enabled: !isDisabled && isOpen,
  })

  const problems = useMemo(
    () => filterPatientProblems(patientIssues, term, chosen),
    [patientIssues, term, chosen],
  )
  const unchosenProcedures = useMemo(() => {
    if (procedures === null) return null
    const chosenKeys = new Set(chosen.map(getPickerKey))
    return procedures.filter(
      (row) => !chosenKeys.has(getPickerKey({ kind: 'procedure', id: row.code_id })),
    )
  }, [procedures, chosen])

  const options = useMemo(
    () => buildPickerOptions({ problems, procedures: unchosenProcedures, showProcedures }),
    [problems, unchosenProcedures, showProcedures],
  )

  // The value type is the search text; the picked option carries the key and entry.
  const handleSelect = (_value: string, option: PickerOption) => {
    const { entry } = option
    if (!entry) return
    onPick(entry)
    setTerm('')
    setIsOpen(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      // The input is type=search: Chrome would clear it and rc-select would reopen on that input.
      event.preventDefault()
      // While the list is open Escape closes only the list; once closed it reaches the dialog.
      if (isOpen) {
        event.stopPropagation()
        setIsOpen(false)
      }
    }
    // Enter never reaches the form: the booking is saved by its own button.
    if (event.key === 'Enter') event.preventDefault()
  }

  const hint = getHint(hasPatient, chosen.length > 0)

  return (
    <div className={styles.picker}>
      <Label id={searchId} label={PICKER_TEXT.label} />
      <AutoComplete<string, PickerOption>
        id={searchId}
        size='large'
        className={styles.select}
        value={term}
        options={options}
        open={isOpen}
        onOpenChange={(open) => !isDisabled && setIsOpen(open)}
        onSearch={setTerm}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        notFoundContent={
          isSearching ? <Spin size='small' /> : getNotFoundMessage(term, patientIssues.length > 0)
        }
        placeholder={PICKER_TEXT.placeholder}
        disabled={isDisabled}
        filterOption={false}
        virtual={false}
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
      />
      <PickerChips chosen={chosen} disabled={disabled} onRemove={onRemove} />
      {hint && <p className={styles.hint}>{hint}</p>}
    </div>
  )
}
